import React, { useEffect, CSSProperties } from 'react';
import { Layout, Grid, Spin, Flex } from 'antd';
import { useAppSelector } from '../../shared/hooks/use-app-selector';
import { useLocalStorageState } from 'ahooks';
import { Header as NewAppHeader } from '../../app/shell/Header';
import { SideMenu } from '../../app/shell/side-menu/SideMenu';

import { useScreenName } from '../../shared/hooks/use-screen-name';
import { FlatSideMenu } from '../../app/shell/flat-side-menu/FlatSideMenu';
import { isWsbMerchant } from '../../selectors';
import { GRAVITY_ACTIVATION_CLASS } from '../../shared/constants/merchant';
import { AIAssistant } from '../../app/shell/AIAssistant';
import { useAIAssistant } from '../../shared/contexts/AIAssistantContext';
import { BETA_TOGGLES } from '../../features/beta/screens/Beta';

const { Content: AntdContent } = Layout;
const { useBreakpoint } = Grid;

// PageLayout Styles
const PAGE_LAYOUT_STYLES = {
  loadingContainer: {
    height: '100vh',
  } as CSSProperties,
  mainContent: {
    display: 'flex',
    margin: '0 auto',
    width: '100%',
    height: '100%',
  } as CSSProperties,
  pageContent: {
    height: '100%',
    flex: 1,
  } as CSSProperties,
  defaultLayout: {
    minHeight: '100vh',
  } as CSSProperties,
};

export function PageLayout({
  children,
  apiClient: _apiClient,
  trackMethod: _trackMethod,
  eventBus: _eventBus,
}: {
  children: JSX.Element;
  apiClient: unknown;
  trackMethod: (
    eventName: string,
    eventPayload?: string | number | object
  ) => void;
  eventBus: unknown;
}) {
  const screens = useBreakpoint();
  const currentMerchant = useAppSelector((state) => state.currentMerchant);
  const isWsb = useAppSelector(isWsbMerchant);

  const screenName = useScreenName();

  const [sideMenuCollapsed, setSideMenuCollapsed] = useLocalStorageState(
    'sideMenuCollapsed',
    {
      defaultValue: !screens.md || false,
      listenStorageChange: true,
    }
  );

  const [showOnboardingAIAssistant] = useLocalStorageState<boolean>(
    BETA_TOGGLES.smartOnboardingAIAssistant.key,
    {
      listenStorageChange: true,
    }
  );

  const { isOpen: isAIAssistantOpen } = useAIAssistant();

  // Collapse menu when screen is smaller than md
  useEffect(() => {
    if (sideMenuCollapsed === undefined) {
      setSideMenuCollapsed(!screens.md);
    }
  }, [screens.md]);

  const merchantId = currentMerchant?.id || '';

  if (!merchantId) {
    return (
      <Layout>
        <Flex
          align="center"
          justify="center"
          style={PAGE_LAYOUT_STYLES.loadingContainer}
        >
          <Spin></Spin>
        </Flex>
      </Layout>
    );
  }

  return isWsb ? (
    <main id="main-content" style={PAGE_LAYOUT_STYLES.mainContent}>
      <div className={GRAVITY_ACTIVATION_CLASS}>
        <FlatSideMenu />
      </div>
      <div className="as-PageContent" style={PAGE_LAYOUT_STYLES.pageContent}>
        {children}
      </div>
    </main>
  ) : (
    <Layout className="as-PageLayout" style={PAGE_LAYOUT_STYLES.defaultLayout}>
      <SideMenu screenName={screenName} sideMenuCollapsed={sideMenuCollapsed} />
      <Layout>
        <NewAppHeader
          sideMenuCollapsed={sideMenuCollapsed}
          onSideMenuCollapseChange={setSideMenuCollapsed}
        />
        <Layout>
          <AntdContent className="as-PageContent">{children}</AntdContent>
          {showOnboardingAIAssistant && isAIAssistantOpen && <AIAssistant />}
        </Layout>
      </Layout>
    </Layout>
  );
}
